import { AlertDialog, Button, Field } from "../components/rumonai";
import { useRef, useState } from "react";
import { ErrorState } from "../shell/ui";
import { hub, type AgentInstance } from "./api";
import "../connections/settings-list.css";

/**
 * 设置 › IP 设置 › 全部管理与设置, in the danger area at its end: deleting this Agent.
 *
 * The Hub deletes only when the request repeats the Agent's exact name
 * (`DELETE /api/hub/agents/{id}`), so the owner types it here -- the one
 * confirmation that cannot be given by accident. The comparison is exact on
 * purpose, the same one the Hub makes: nothing is trimmed.
 *
 * Nothing leaves the owner's list until the Hub has answered. A refusal is
 * shown in the Hub's own sentences, keeps what was typed, and the same request
 * can be sent again. Once the Hub has answered, `onDeleted` is the only thing
 * said: leaving the page and taking the Agent out of the list are one step the
 * Hub takes together (`OwnerSettings`). Cloud and local Agents send the same request; what the
 * cleanup involves is the Hub's to decide.
 */
export function AgentDeletion({ agent, onDeleted }: {
  /** The Hub's name for it is the one the Hub compares what was typed with. */
  agent: Pick<AgentInstance, "id" | "name"> & Partial<Pick<AgentInstance, "location" | "client_id">>;
  onDeleted?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const submitting = useRef(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const matches = typed === agent.name;
  const consequence = agent.location === "local" ? agent.client_id
    ? "将删除它的对话、记忆和工作文件。"
    : "将删除网页记录和连接，本机文件保留。" : "删除后无法撤销。";

  const close = () => {
    setOpen(false);
    setTyped("");
    setError(null);
  };
  const submit = async () => {
    if (submitting.current || busy || !matches) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await hub.deleteAgent(agent.id, typed);
    } catch (failure) {
      setError(failure);
      submitting.current = false;
      setBusy(false);
      return;
    }
    // Where to go next is the caller's: this page belongs to an Agent that no longer exists, and the
    // frame this runs in cannot take the owner to the Hub's list by itself.
    onDeleted?.(agent.id);
  };
  const field = `agent-delete-${agent.id}`;
  return <section className="settings-block settings-agent-delete settings-danger" aria-label="删除 Agent">
    <div><h3>删除这个 IP</h3><p>{consequence}</p></div>
    <AlertDialog open={open} onOpenChange={next => { if (!submitting.current) { if (next) setOpen(true); else close(); } }}
      title="删除这个 IP" description={consequence + "输入这个 IP 的完整名称后才能删除。"}
      confirmLabel={busy ? "正在删除…" : "删除"} pending={busy} disabled={!matches}
      onConfirm={() => { void submit(); }}
      trigger={<Button variant="danger" name="delete-agent" aria-label="删除这个 IP">删除</Button>}>
      <form onSubmit={event => { event.preventDefault(); void submit(); }} aria-busy={busy}>
        <Field id={field} label={<>输入「<span data-quoted="true">{agent.name}</span>」确认</>}>
          <input name="delete-agent-name" autoComplete="off" spellCheck={false}
            value={typed} onChange={event => setTyped(event.target.value)} disabled={busy} />
        </Field>
        <button type="submit" hidden tabIndex={-1} aria-hidden="true" disabled={busy || !matches} />
        {error ? <ErrorState error={error} /> : null}
      </form>
    </AlertDialog>
  </section>;
}
